<div class="w-full max-w-full relative">
    <div class="mb-8 relative z-20">
        <div class="card-bg rounded-2xl p-6">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 class="text-2xl font-medium text-[#141437] dark:text-white">
                        Role Management
                    </h1>
                    <p class="text-slate-600 dark:text-slate-400 mt-1">
                        Define and manage application roles and their product access
                    </p>
                </div>
                <app-button variant="default" (buttonClick)="openPanel()" [iconName]="'circlePlus'"
                    className="flex items-center justify-center gap-2 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_4px_8px_-4px_#AD46FF] transition-all bg-[#AD46FF] hover:bg-[#AD46FF] border-none text-white">
                    <span class="font-medium whitespace-nowrap">Add Role</span>
                </app-button>
            </div>
        </div>
    </div>

    <div class="mb-6 relative z-30 mx-auto">
        <div class="card-bg rounded-2xl p-6">
            <div class="flex flex-col lg:flex-row gap-4">

                <!-- Search -->
                <div class="flex-1 relative z-40">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Search
                    </label>
                    <div class="relative">
                        <div
                            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400/80">
                            <app-icon name="search" [size]="17"></app-icon>
                        </div>
                        <input type="text" placeholder="Search role name"
                            class="w-full h-[41px] pl-11 pr-4 bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all dark:text-white placeholder:text-slate-400/50"
                            [ngModel]="searchTerm()" (input)="onSearch(searchInput.value)"
                            (keyup.enter)="applyFilters()" #searchInput>
                    </div>
                </div>


                <!-- Status -->
                <div class="flex-1 relative z-20">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Status
                    </label>
                    <app-dropdown [options]="statusOptions" [selectedValue]="selectedStatus()" selectionMode="single"
                        placeholder="Filter by Status" [fullWidth]="true" (selectionChange)="onStatusFilter($event)"
                        class="w-full relative z-50" />
                </div>

                <div class="flex items-end z-10 gap-3">
                    <app-button variant="primary" (buttonClick)="applyFilters()"
                        className="h-[41px] px-6 text-white bg-[#AD46FF] hover:bg-[#AD46FF]/90 transition-all duration-200 shadow-md shadow-[#AD46FF]/20 rounded-xl">
                        <span class="font-medium whitespace-nowrap">Apply Filters</span>
                    </app-button>

                    <app-button [disabled]="!selectedStatus() && !searchTerm()" variant="secondary"
                        (buttonClick)="clearFilters()"
                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 rounded-xl transition-all duration-200">
                        Clear Filters
                    </app-button>
                </div>
            </div>
        </div>
    </div>

    <!-- Loading State -->
    @if (isLoading()) {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg relative z-10">
        <div class="flex items-center justify-center">
            <div class="flex items-center gap-3">
                <div class="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span class="text-slate-600 dark:text-slate-400 font-medium">Loading roles...</span>
            </div>
        </div>
    </div>
    }

    <!-- Roles Table -->
    @if (!isLoading() && processedRoleData.length > 0) {
    <div class="card-bg rounded-2xl overflow-hidden relative z-10">
        <div
            class="px-6 py-4 bg-gradient-to-r from-slate-50/50 to-blue-50/50 dark:from-slate-700/50 dark:to-slate-600/50 border-b border-slate-200/50 dark:border-slate-600/50">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-200">
                Role List
            </h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {{ totalItems() }} total roles found
            </p>
        </div>

        <div class="p-6">
            <app-paginated-table (actionSelected)="selectedRowItem($event)" [tableData]="processedRoleData"
                [headers]="tableHeaders" [isCheckboxShow]="false" [totalItems]="totalItems()" [pageSize]="pageSize()"
                [currentPage]="page()" (pageChange)="onPageChange($event)" [serverSide]="true"
                [tableActionOptions]="{showToggleButton: true, toggleOptions:tableActionOptions}" />
        </div>
    </div>
    }

    <!-- Empty State -->
    @if (!isLoading() && processedRoleData.length === 0) {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg text-center relative z-10">
        <div class="max-w-md mx-auto">
            <div
                class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                <app-icon name="shield" [size]="32" class="text-blue-600 dark:text-blue-400"></app-icon>
            </div>
            <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                No Roles Found
            </h3>
            <p class="text-slate-600 dark:text-slate-400 mb-6">
                There are no roles matching your current filters.
            </p>
        </div>
    </div>
    }

    <!-- Error Message -->

    <!-- Slide Panel -->
    @if (panelOpen()) {
    <app-slide-panel [open]="panelOpen()" [title]="isEditing() ? 'Edit Role' : 'Add New Role'" (closed)="closePanel()"
        class="flex-1 text-[#141437] dark:text-white"
        panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
        <form [formGroup]="form" (ngSubmit)="submitForm()" class="flex flex-col flex-1 overflow-hidden h-full">
            <section class="flex flex-col gap-8 flex-1 overflow-y-auto p-6">
                <!-- Role Details Section -->
                <div class="space-y-4">
                    <h3
                        class="text-md font-semibold text-slate-800 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2 mb-6">
                        Role Details </h3>
                    <div class="grid grid-cols-1 gap-4">
                        <app-input variant="themeBase" placeholder="Enter role name" label="Role Name"
                            formControlName="name" [error]="getErrorMessage('name')" [maxlength]="50"
                            className="uppercase" (input)="onNameInput($event)"
                            (keydown)="preventLeadingSpace($event)"></app-input>

                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-medium text-slate-600 dark:text-ink-muted">Description</label>
                            <textarea formControlName="description" placeholder="Enter role description" maxlength="150"
                                class="w-full p-3 bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all dark:text-white placeholder:text-slate-400/50 min-h-[80px] resize-none"></textarea>
                        </div>
                    </div>
                </div>

                <div class="space-y-4">
                    <h3
                        class="text-md font-semibold text-slate-800 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2 mb-6">
                        Product Based Access Management
                    </h3>

                    <div
                        class="bg-slate-50 dark:bg-surface-0 border border-slate-200 dark:border-white/5 rounded-3xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col">
                        <!-- Segmented Toggle Header -->
                        <div
                            class="p-2 flex gap-1 bg-white dark:bg-surface-2 border-b border-slate-200 dark:border-white/5">
                            <button type="button"
                                (click)="form.get('productScope')?.setValue('ALL_PRODUCTS'); onInputChange('productScope')"
                                [class]="form.get('productScope')?.value === 'ALL_PRODUCTS' ? 'bg-[#AD46FF] text-white shadow-[0_0_20px_rgba(173,70,255,0.3)]' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-700 dark:hover:text-slate-200'"
                                class="flex-1 py-3 text-xs font-bold rounded-2xl transition-all duration-300 ease-out uppercase tracking-wider">
                                Full Access
                            </button>
                            <button type="button"
                                (click)="form.get('productScope')?.setValue('LIMITED_PRODUCTS'); onInputChange('productScope')"
                                [class]="form.get('productScope')?.value === 'LIMITED_PRODUCTS' ? 'bg-[#AD46FF] text-white shadow-[0_0_20px_rgba(173,70,255,0.3)]' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-700 dark:hover:text-slate-200'"
                                class="flex-1 py-3 text-xs font-bold rounded-2xl transition-all duration-300 ease-out uppercase tracking-wider">
                                Limited Access
                            </button>
                        </div>

                        <!-- Header Info (Only show for Limited Access) -->
                        @if (form.get('productScope')?.value === 'LIMITED_PRODUCTS') {
                        <div
                            class="p-6 bg-slate-50/50 dark:bg-white/[0.01] border-b border-slate-200 dark:border-white/5">
                            <div
                                class="flex items-center justify-between text-[11px] font-black text-slate-500 uppercase tracking-widest px-1">
                                <span>Product Name</span>
                                <div class="flex items-center gap-2">
                                    <span>Toggle Access</span>
                                    <button type="button" (click)="toggleAllProductsLocal()"
                                        class="text-[#AD46FF] hover:text-[#AD46FF]/80 normal-case font-bold ml-2 cursor-pointer">
                                        {{ isAllProductsSelected() ? 'Unselect All' : 'Select All' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        }

                        <!-- Scrollable Product List (Only show for Limited Access) -->
                        @if (form.get('productScope')?.value === 'LIMITED_PRODUCTS') {
                        <div class="max-h-[380px] overflow-y-auto custom-scrollbar bg-white dark:bg-surface-0">
                            @for (product of filteredProducts(); track product.id) {
                            <div (click)="onProductToggle(product)"
                                class="px-6 py-5 flex items-center justify-between border-b border-slate-100 dark:border-white/[0.03] transition-all cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02]">

                                <div class="flex items-center gap-4">
                                    <div
                                        class="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-white/5 flex items-center justify-center text-[#AD46FF] border border-slate-200 dark:border-white/10 shadow-inner group-hover:scale-110 transition-transform duration-300">
                                        <app-icon name="zap" [size]="20"></app-icon>
                                    </div>
                                    <div class="flex flex-col">
                                        <span
                                            class="text-[14px] font-bold tracking-tight text-slate-800 dark:text-white">{{
                                            product.name || product.displayName || product.label }}</span>
                                        <span
                                            class="text-[11px] text-slate-400 dark:text-slate-500 font-medium">Standard
                                            License</span>
                                    </div>
                                </div>

                                <div class="flex items-center">
                                    <div class="w-7 h-7 rounded-xl border-2 transition-all duration-300 flex items-center justify-center"
                                        [ngClass]="{
                                              'bg-[#AD46FF] border-[#AD46FF] shadow-[0_0_15px_rgba(173,70,255,0.4)]': isProductSelected(product),
                                              'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5': !isProductSelected(product)
                                          }">
                                        @if (isProductSelected(product)) {
                                        <app-icon name="check" [size]="16" class="text-white fill-white"></app-icon>
                                        }
                                    </div>
                                </div>
                            </div>
                            } @empty {
                            <div class="py-16 text-center">
                                <div
                                    class="w-16 h-16 mx-auto mb-4 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-700">
                                    <app-icon name="search" [size]="24"></app-icon>
                                </div>
                                <p class="text-sm text-slate-500 dark:text-slate-400 font-medium">No products found
                                    matching
                                    your search</p>
                            </div>
                            }
                        </div>
                        }

                        <!-- Intelligent Info Bar -->
                        <div class="bg-slate-50 dark:bg-surface-2 p-5 border-t border-slate-200 dark:border-white/5">
                            @if (form.get('productScope')?.value === 'ALL_PRODUCTS') {
                            <div class="flex gap-3 items-center text-[#10B981]">
                                <div class="w-2.5 h-2.5 rounded-full bg-[#10B981] shadow-[0_0_8px_#10B981]"></div>
                                <span class="text-[12px] font-bold leading-tight">Universal access enabled. All platform
                                    capabilities are unlocked.</span>
                            </div>
                            } @else {
                            <div class="flex justify-between items-center">
                                <span class="text-[12px] text-slate-500 dark:text-slate-400 font-bold italic">
                                    {{ selectedCount() }} of {{ allProducts().length }} products selected
                                </span>
                            </div>
                            }
                        </div>
                    </div>
                </div>


            </section>

            <!-- Footer -->
            <div
                class="grid grid-cols-2 gap-4 p-5 sticky bottom-0 bg-white dark:bg-surface-1 border-t border-[#EAEAEA] dark:border-line mt-auto">
                <app-button type="button" variant="defaultBorder" (buttonClick)="closePanel()"
                    className="w-full h-[45px]">
                    Cancel
                </app-button>
                <app-button type="submit" variant="default" className="w-full h-[45px]"
                    [disabled]="isLoading() || form.invalid">
                    {{ isLoading() ? 'Saving...' : 'Save' }}
                </app-button>
            </div>
        </form>
    </app-slide-panel>
    }

    <!-- Audit Logs -->
    <div class="mt-12 pb-12 relative z-10">
        <app-audit-log-panel [entityType]="auditEntityTypes" [showProductFilter]="false"
            title="User Roles Audit Trails">
        </app-audit-log-panel>
    </div>

</div>